import { useMutation } from "@apollo/client";
import { Box, Paper, Stack, useTheme } from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import { DOCUMENT_WITH_METADATA } from "components/Document/ReviewDocumentMetadataDialog/queries";
import { TextInput } from "components/TextInput";
import { gql } from "generated-graphql";
import {
  CommentThreadFragment,
  EventMutationScope,
  TaskStatus,
  UpdateTaskCoreInput,
} from "generated-graphql/graphql";
import { useDeleteDocumentMutation } from "hooks/documents";
import { DateTime } from "luxon";
import { useCallback, useEffect, useRef } from "react";
import { Controller, FormProvider, Resolver, useForm, useFormState } from "react-hook-form";

import { DocumentsListControl } from "./DocumentsControl";
import useDeleteTaskMutation from "./hooks/useDeleteTask";
import { useUpdateTaskMutation } from "./hooks/useUpdateTask";
import { LinksControl } from "./LinksControl";
import SubtasksControl from "./SubtasksControl";
import { TaskCommentSection } from "./TaskCommentSection";
import { TaskDialogSkeleton } from "./TaskDialogSkeleton";
import { TaskPickerToolbar } from "./TaskPickerToolbar";
import { TaskTitleHeader } from "./TaskTitleHeader";
import { TaskFormType } from "./types";

const UPDATE_TASK = gql(`
  mutation UpdateTask($input: UpdateTaskCoreInput!) {
    updateTaskCore(input: $input) {
      id
    }
  }
`);

export type TaskSyncFromDocumentPayload = {
  facilityIds: string[];
  /** Document metadata due date (ISO date string from DateField, e.g. yyyy-MM-dd) */
  dueDateIso: string | null;
};

export interface TaskFormProps {
  initialValues: TaskFormType;
  isLoading: boolean;
  validationResolver?: Resolver<TaskFormType>;

  // UI configuration
  /** Show view in tasks button */
  showViewInTasks?: boolean;
  /** Virtual event id used for occurrence-level comments */
  virtualEventId?: string;
  /**
   * When true the status picker is editable and status changes persist for the
   * single occurrence via updateTaskSingle. Defaults to false (status is read-only).
   */
  editableStatus?: boolean;
  /** Render the task comment thread below the form. */
  showCommentsSection?: boolean;
  /** Existing comments for the virtual event. */
  comments?: CommentThreadFragment[];
  /**
   * When true with syncFromDocument, the first payload is treated as baseline only;
   * later payload changes copy facilityIds + deadline onto the task and autosave.
   */
  syncTaskFromDocumentDetails?: boolean;
  syncFromDocument?: TaskSyncFromDocumentPayload;
}

/**
 * A form for editing a task that autosaves changes to the task (not virtual event).
 */
function facilityIdsMatch(a: string[], b: string[]): boolean {
  if (a.length !== b.length) {
    return false;
  }
  const sortedA = [...a].sort();
  const sortedB = [...b].sort();
  return sortedA.every((id, i) => id === sortedB[i]);
}

function deadlineMatchesDueDateIso(deadline: Date | null, dueDateIso: string | null): boolean {
  if (dueDateIso === null && deadline === null) {
    return true;
  }
  if (dueDateIso === null || deadline === null) {
    return false;
  }
  const fromTask = DateTime.fromJSDate(deadline).toISODate();
  const fromDoc = DateTime.fromISO(dueDateIso).toISODate();
  return fromTask === fromDoc;
}

export const AutosaveEditTaskForm: React.FC<TaskFormProps> = ({
  initialValues,
  isLoading,
  validationResolver,
  showViewInTasks = false,
  virtualEventId,
  editableStatus = false,
  showCommentsSection = false,
  comments = [],
  syncTaskFromDocumentDetails = false,
  syncFromDocument,
}) => {
  const theme = useTheme();
  const alerts = useAlerts();
  const form = useForm<TaskFormType>({
    defaultValues: initialValues,
    resolver: validationResolver,
    mode: "onChange",
  });
  const { control, watch } = form;
  const { isDirty, isSubmitting } = useFormState({ control });
  const documentSyncBaselineJsonRef = useRef<string | null>(null);
  const documentSyncTaskIdRef = useRef(initialValues.taskId);

  const completedOn = watch("completedOn");
  const completionTiming = watch("completionTiming");
  const completionDeltaDays = watch("completionDeltaDays");
  const taskStatus = watch("taskStatus");
  const isSuggested = watch("isSuggested");

  const [updateTask] = useMutation(UPDATE_TASK);
  const [updateTaskSingle] = useUpdateTaskMutation(
    initialValues.taskId
      ? {
          id: virtualEventId ?? initialValues.taskId,
          frequency: initialValues.frequency,
          seriesId: initialValues.seriesId ?? "",
        }
      : null,
    initialValues.tenantId ?? ""
  );
  const lastPersistedStatusRef = useRef<TaskStatus | null>(initialValues.taskStatus);

  useEffect(() => {
    lastPersistedStatusRef.current = initialValues.taskStatus;
  }, [initialValues.taskId, initialValues.taskStatus]);

  const persistTaskCore = useCallback(
    async (data: TaskFormType) => {
      if (!data.taskId || !data.tenantId) {
        return;
      }
      const input: UpdateTaskCoreInput = {
        tenantId: data.tenantId ?? "",
        taskId: data.taskId ?? "",
        title: data.title,
        description: data.description,
        startDate: data.deadline ?? undefined,
        facilityIds: data.facilityIds,
        assigneeId: data.assignee?.id ?? undefined,
        watcherIds: data.watchers.map((watcher) => watcher.id),
        documentIds: data.documents.map((document) => document.id),
        reminderDays: data.reminderDays.map((reminder) => reminder.days),
        subtasks: data.subtasks.map((subtask) => ({
          id: subtask.id,
          title: subtask.title,
        })),
        links: data.links.map((link) => link.url),
        frequency: data.frequency,
        interval: data.interval,
        tags: data.tags,
      };
      await updateTask({ variables: { input } });
    },
    [updateTask]
  );

  const onSubmit = useCallback(
    async (data: TaskFormType) => {
      if (!data.taskId || !data.tenantId || !isDirty) {
        return;
      }
      await persistTaskCore(data);
    },
    [isDirty, persistTaskCore]
  );

  useEffect(() => {
    if (initialValues.taskId !== documentSyncTaskIdRef.current) {
      documentSyncTaskIdRef.current = initialValues.taskId;
      documentSyncBaselineJsonRef.current = null;
    }
  }, [initialValues.taskId]);

  useEffect(() => {
    if (!syncTaskFromDocumentDetails) {
      documentSyncBaselineJsonRef.current = null;
    }
  }, [syncTaskFromDocumentDetails]);

  useEffect(() => {
    if (!editableStatus || !taskStatus) {
      return;
    }
    if (lastPersistedStatusRef.current === taskStatus) {
      return;
    }
    if (!initialValues.taskId || !initialValues.tenantId || !initialValues.deadline) {
      return;
    }

    const previousStatus = lastPersistedStatusRef.current;
    lastPersistedStatusRef.current = taskStatus;

    void updateTaskSingle({
      variables: {
        taskData: form.getValues(),
        scope: EventMutationScope.This,
      },
      onError: (err) => {
        lastPersistedStatusRef.current = previousStatus;
        if (previousStatus !== null) {
          form.setValue("taskStatus", previousStatus, { shouldDirty: false });
        }
        alerts.error("Failed to update task status", err);
      },
    });
  }, [
    alerts,
    editableStatus,
    form,
    initialValues.deadline,
    initialValues.taskId,
    initialValues.tenantId,
    taskStatus,
    updateTaskSingle,
  ]);

  useEffect(() => {
    if (!syncTaskFromDocumentDetails || !syncFromDocument) {
      return;
    }

    const payloadJson = JSON.stringify({
      f: [...syncFromDocument.facilityIds].sort(),
      d: syncFromDocument.dueDateIso,
    });

    if (documentSyncBaselineJsonRef.current === null) {
      documentSyncBaselineJsonRef.current = payloadJson;
      return;
    }

    if (documentSyncBaselineJsonRef.current === payloadJson) {
      return;
    }

    documentSyncBaselineJsonRef.current = payloadJson;

    const nextDeadline =
      syncFromDocument.dueDateIso !== null
        ? DateTime.fromISO(syncFromDocument.dueDateIso).startOf("day").toJSDate()
        : null;

    const currentFacilityIds = form.getValues("facilityIds");
    const currentDeadline = form.getValues("deadline");

    const facilitiesAlreadyMatch = facilityIdsMatch(
      currentFacilityIds,
      syncFromDocument.facilityIds
    );
    const deadlineAlreadyMatch = deadlineMatchesDueDateIso(
      currentDeadline,
      syncFromDocument.dueDateIso
    );

    if (facilitiesAlreadyMatch && deadlineAlreadyMatch) {
      return;
    }

    form.setValue("facilityIds", syncFromDocument.facilityIds, { shouldDirty: true });
    form.setValue("deadline", nextDeadline, { shouldDirty: true });
    void persistTaskCore(form.getValues());
  }, [form, persistTaskCore, syncFromDocument, syncTaskFromDocumentDetails]);

  const handleFormBlur = useCallback(
    (event: React.FocusEvent<HTMLElement>) => {
      const related = event.relatedTarget;
      if (related instanceof Node && event.currentTarget.contains(related)) {
        return;
      }
      if (!isDirty || isSubmitting) {
        return;
      }
      void form.handleSubmit(onSubmit)();
    },
    [form, onSubmit, isDirty, isSubmitting]
  );

  const [deleteTask] = useDeleteTaskMutation();
  const onDeleteTask = useCallback(
    (scope: EventMutationScope) => {
      deleteTask({
        variables: {
          input: {
            taskId: initialValues.taskId ?? "",
            date: initialValues.deadline ?? undefined,
            eventMutationScope: scope,
          },
        },
        refetchQueries: [DOCUMENT_WITH_METADATA],
      });
    },
    [deleteTask, initialValues.taskId, initialValues.deadline]
  );

  const [deleteDocument] = useDeleteDocumentMutation({
    onError: () => {
      alerts.error("Failed to delete document");
    },
  });

  return (
    <Paper
      elevation={0}
      sx={{ border: `1px solid ${theme.palette.grey[300]}` }}
      onBlur={handleFormBlur}
    >
      <FormProvider {...form}>
        {isLoading ? (
          <TaskDialogSkeleton />
        ) : (
          <Stack my={2} mx={3} gap={theme.spacing(2.5)} sx={{ pb: 2 }}>
            <TaskTitleHeader
              control={control}
              initialValues={initialValues}
              completedOn={completedOn}
              completionTiming={completionTiming}
              completionDeltaDays={completionDeltaDays}
              taskStatus={taskStatus}
              isSuggested={isSuggested}
              onDeleteTask={() => onDeleteTask(EventMutationScope.All)}
              showViewInTasks={showViewInTasks}
              virtualEventId={virtualEventId}
              readonlyStatusPicker={!editableStatus}
            />

            <Stack gap={theme.spacing(2)}>
              <Controller
                name="description"
                control={control}
                render={({ field }) => (
                  <TextInput
                    value={field.value ?? ""}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    variant="description"
                    placeholder="Add task description..."
                    multiline={true}
                    maxRows={10}
                  />
                )}
              />

              <SubtasksControl control={control} />
              <DocumentsListControl
                control={control}
                onDelete={(id: string) => deleteDocument({ variables: { id } })}
              />
              <LinksControl control={control} />
            </Stack>

            <Box
              sx={{
                px: 1.25,
                py: 1.25,
                borderRadius: 2,
                bgcolor: "grey.50",
                border: (theme) => `1px solid ${theme.palette.grey[200]}`,
              }}
            >
              <Stack direction="row" gap={theme.spacing(1)} width="100%" flexWrap="wrap">
                <TaskPickerToolbar form={form} showActionButtons={false} />
              </Stack>
            </Box>

            {showCommentsSection && (
              <TaskCommentSection virtualEventId={virtualEventId} comments={comments} />
            )}
          </Stack>
        )}
      </FormProvider>
    </Paper>
  );
};
